import React, { useCallback, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import yup from '../../../../utility/yup';
import { ItemForm } from '../../../../components/index';
import { Article } from '../../../../types/items';
import { MediaFormDocument, MediaFormVideo } from '../../../../types/formItems';
import {
  saveArticleDocumentRequest,
  getArticleRequest,
  saveArticleVideoRequest,
  setArticleFormsSubmit,
  setCurrentDocumentPageIndexMediaViewer,
  getArticleVideoMasterRequest,
} from '../../../../store/modules/articles/actions';
import MediaFormProvider from './MediaFormProvider';
import {
  MediaImage,
  MediaOthers,
  MediaPaged,
  MediaVideo,
  MediaAudio,
} from './media-types/index';
import { SseContext } from '../../../../services/SSE';
import { getAllQueryData } from '../../../../utility/helpers';
import { notify } from '../../../../store/modules/notifications/actions';
import { updateQuery } from './utils';
import useMediaItemWatcher from '../../../../hooks/useMediaItemWatcher';

type Props = {
  media: Article;
};

const schema = yup.object().shape({
  title: yup.string().required().trim(),
});

/*
  {key: 0, value: "Other"}
  {key: 1, value: "Image"}
  {key: 2, value: "Video"}
  {key: 3, value: "Audio"}
  {key: 4, value: "Paged"}
*/

const MediaItem = ({ media }: Props) => {
  const dispatch = useDispatch();
  const { addSource } = useContext(SseContext);
  let mediaForm: MediaFormDocument = {
    id: media.document_id,
    title: (media.ref.document) ? media.ref.document.title : '',
  };
  const mediaId = media.id;
  const queryData = getAllQueryData();
  if (media.video_id !== -1) {
    mediaForm = {
      id: media.video_id,
      title: media.ref.video!.title,
    } as MediaFormVideo;
  }
  useMediaItemWatcher(media);

  const saveHandler = useCallback((data: MediaFormDocument | MediaFormVideo) => {
    if (media.video_id !== -1) {
      dispatch(saveArticleVideoRequest(data));
    } else {
      dispatch(saveArticleDocumentRequest(data));
    }
  }, [dispatch, media.video_id]);

  const openMediaPreview = useCallback(() => {
    dispatch(setCurrentDocumentPageIndexMediaViewer(1));

    queryData.preview = mediaId;
    // delete queryData.documentPageId;
    updateQuery(queryData);
  }, [dispatch, queryData, mediaId]);

  useEffect(() => {
    const refDocument = media.ref.document;
    const refVideo = media.ref.video;
    if (refDocument || refVideo) {
      const type = refDocument?.media_type_id;
      const status = refVideo?.ref?.upload_status.id || 3;
      if (
        ((type === 1 || type === 4) && refDocument?.thumbnails?.length === 0)
        || (refVideo && (status < 4))
      ) {
        const sseTopic = refDocument?.ref?.sse_topic || refVideo?.ref?.sse_topic;
        if (sseTopic) {
          addSource({
            url: sseTopic,
            onMessage: (data) => {
              switch (data.action) {
                case 'processing_completed':
                  dispatch(getArticleRequest(media.id));
                  break;
                case 'video.master.preparing':
                  dispatch(notify({
                    type: 'success',
                    message: 'The master file preparation has been started and it will be downloaded when ready',
                  }));
                  break;
                case 'video.master.ready':

                  if (refVideo) {
                    dispatch(getArticleVideoMasterRequest(refVideo));
                  }
                  break;
                default: break;
              }
            },
          });
        }
      }
    }
  }, [dispatch, addSource, media]);

  const keyUpHandler = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      dispatch(setArticleFormsSubmit(true));
    }
  }, [dispatch]);

  const render = () => {
    const typeId = media.ref.document
      ? media.ref.document.media_type_id
      : media.ref.video ? 2 : null;

    switch (typeId) {
      case 1:
        return <MediaImage media={media} openMediaPreview={openMediaPreview} keyUpHandler={keyUpHandler} />;
      case 2:
        return <MediaVideo media={media} openMediaPreview={openMediaPreview} keyUpHandler={keyUpHandler} />;
      case 3:
        return <MediaAudio media={media} openMediaPreview={openMediaPreview} keyUpHandler={keyUpHandler} />;
      case 4:
        return <MediaPaged media={media} openMediaPreview={openMediaPreview} keyUpHandler={keyUpHandler} />;
      case 0:
        return <MediaOthers media={media} openMediaPreview={openMediaPreview} keyUpHandler={keyUpHandler} />;
      default:
        return <p>Media type is not supported</p>;
    }
  };

  return (
    <ItemForm
      enableReinitialize
      initialValues={mediaForm}
      onSubmit={saveHandler}
      validationSchema={schema}
      loading={false}
      hideActions
      key={media.id}
    >
      <MediaFormProvider id={mediaForm.id} />
      {render()}
    </ItemForm>
  );
};

export default MediaItem;
